import { useSelector } from 'react-redux';
import map from 'lodash/map';
import uniqueId from 'lodash/uniqueId';
import { NextPage } from 'next';

import { tariffs } from '@r1-frontend/api-domru/api-content/v1/cities/tariffs';
import {
    TResponseDto as TTariffsResponseDto,
} from '@r1-frontend/api-domru/api-content/v1/cities/tariffs/dto/ResponseDto';
import constructorPrice from '@r1-frontend/api-domru/api-tariffs/v1/tariff/constructor-price';
import {
    ResponseDto as TConstructorPriceResponseDto,
} from '@r1-frontend/api-domru/api-tariffs/v1/tariff/constructor-price/dto/ResponseDto';
import { IResult } from '@r1-frontend/api-domru/content-static/dto/ResponseDto';
import { getArticlesByPlace } from '@r1-frontend/api-domru/content-static/v2/articles';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { getBanners } from '~/src/api/content/banners';
import { getPageView } from '~/src/api/content/page/view';
import { createTariffBuilder } from '~/src/components/Bundles/helpers';
import { ITariff } from '~/src/entities/formattedTariff';
import Layout from '~/src/features/_layout';
import { SeoScriptAggregateRating, SeoScriptAggregateRatingWithOffer } from '~/src/features/_layout/SeoScripts';
import { ISeoScriptAggregateRating } from '~/src/features/_layout/SeoScripts/SeoScriptAggregateRating';
import { generateTariffSummaryTableFAQ } from '~/src/features/internet/generateTariffSummaryTableFAQ';
import { articlesToKeyDataMap } from '~/src/features/internet/generateTariffSummaryTableFAQ/utils/articlesToKeyDataMap';
import { InternetRepresent } from '~/src/features/internet/internet-represent';
import { IRepresentTopProps } from '~/src/features/internet/internet-represent/page-blocks/AddOpportunities';
import { dataQuestions } from '~/src/features/internet/internet-represent/page-blocks/Questions/data-questions/data';
import { getTitleAndDescription } from '~/src/features/internet/utils/getMetatags';
import { Internet } from '~/src/features/internetV2';
import { REDESIGN_INTERNET } from '~/src/features/internetV2/constants/abnVariants';
import { setArticles } from '~/src/features/internetV2/store/actions';
import { articlesFormatter } from '~/src/features/internetV2/store/actions/thunk/getFAQ';
import { getABNVariantOnSSr } from '~/src/helpers/abn-tests';
import { initialProps } from '~/src/helpers/basePageFunc';
import { getCookie } from '~/src/helpers/cookie';
import getRequester from '~/src/helpers/getRequester';
import { nonPermanentRedirect } from '~/src/helpers/url';
import { saveTariffs } from '~/src/store/actions/bundles';
import { setBannerForInternet, setTariffsForInternet } from '~/src/store/actions/internet';
import { getCurrentCity, selectCompanyName } from '~/src/store/selectors/city';
import {
    selectAndSortAvailableOnInternetPageTariffs,
    selectMinCostedTariff,
} from '~/src/store/selectors/filteredBundles';
import selectProviderId from '~/src/store/selectors/selectProviderId';

const ADDITIONAL_OPPORTUNITIES_ID = '2911';

type TInternetPageProps = {
    currentCity: ICurrentCity,
    addOpportunities: IRepresentTopProps['addOpportunities'] | null,
    companyName: string,
    isRedesignInternetAbTestActive?: boolean,
}

const InternetPage: NextPage<TInternetPageProps> = ({
    isRedesignInternetAbTestActive,
    addOpportunities,
    currentCity,
    companyName,
}): JSX.Element => {
    const minCostedTariff = useSelector(selectMinCostedTariff((tariff: ITariff) => tariff.availableOnMainPage));

    const baseSeoData: ISeoScriptAggregateRating = {
        name: `Новый интернет ${companyName}: стабильный, быстрый, умный`,
        ratingCount: '1987',
        ratingValue: '4.7',
    };

    const cityText = currentCity.cityIn ? currentCity.cityIn : '';
    const { title, description } = getTitleAndDescription(cityText, companyName, currentCity.phone);

    return (
        <>
            {minCostedTariff
                ? <SeoScriptAggregateRatingWithOffer
                    {...minCostedTariff}
                    {...baseSeoData}
                />
                : <SeoScriptAggregateRating {...baseSeoData}/>
            }
            <Layout
                title={title}
                description={description}
                withMenu={true}
                withChat={true}
                withFooter={!isRedesignInternetAbTestActive}
                bgColor={COLORS.BgMain}
            >
                {
                    isRedesignInternetAbTestActive
                        ? <Internet/>
                        : <InternetRepresent
                            currentCity={currentCity}
                            addOpportunities={addOpportunities}
                        />
                }
            </Layout>
        </>
    );
};

InternetPage.getInitialProps = async(options) => {
    const { validatedAccessToken } = await initialProps(options, { isNeedAuthData: true });

    const { store, req, res, query: { code } } = options;

    if (validatedAccessToken) {
        nonPermanentRedirect({ res, location: '/lk/change-tariff' });
    }

    const state = store.getState();
    const {
        internet: {
            tariffs: internetTariffs,
        },
        bundles: {
            tariffs: { allIds: bundlesTariffs },
        },
    } = state;
    const isRedesignInternetAbTestActive = getABNVariantOnSSr(REDESIGN_INTERNET.testName, req) === REDESIGN_INTERNET.variants.on;

    const providerId = Number(getCookie('providerId', req) || selectProviderId(state));

    const currentCity = getCurrentCity(state);
    const companyName = selectCompanyName(state);

    const hasTariffs = !!internetTariffs?.length && !!bundlesTariffs?.length;

    const doRequest = getRequester(req);

    const [
        respAddOpportunities,
        bannersResponse,
        tariffsResponse,
        constructorPriceResponse,
        articlesResponse,
    ] = await Promise.all([
        getPageView(providerId, ADDITIONAL_OPPORTUNITIES_ID),
        getBanners({ providerId, isAuth: !!validatedAccessToken, tag: 'internet' }),
        hasTariffs
            ? null
            : doRequest.apiContent<TTariffsResponseDto>({
                ...tariffs({ providerId }),
                withProvider: false,
            }),
        doRequest.apiTariffs<TConstructorPriceResponseDto>(constructorPrice(providerId)),
        isRedesignInternetAbTestActive
            ? doRequest.apiContentStatic<IResult>(
                getArticlesByPlace({
                    providerId,
                    placeAlias: 'internetPage',
                }),
            )
            : null,
    ]);

    if (bannersResponse.isSuccess) {
        store.dispatch(setBannerForInternet(bannersResponse.payload[0]?.bannerInfo));
    }

    if (constructorPriceResponse?.isSuccess && tariffsResponse?.isSuccess) {
        const mono = [
            ...tariffsResponse.payload.mono,
            createTariffBuilder(constructorPriceResponse.payload.mono),
        ];

        const bundle = {
            ...tariffsResponse.payload.bundles,
            [uniqueId()]: createTariffBuilder(constructorPriceResponse.payload.bundle),
        };

        store.dispatch(setTariffsForInternet(mono));
        store.dispatch(saveTariffs(bundle));
    }

    if (!constructorPriceResponse?.isSuccess && tariffsResponse?.isSuccess) {
        store.dispatch(setTariffsForInternet(tariffsResponse.payload.mono));
        store.dispatch(saveTariffs(tariffsResponse.payload.bundles));
    }

    let articles = articlesResponse === null ? dataQuestions : [];
    if (articlesResponse?.isSuccess) {
        const articlesResult = articlesFormatter(articlesResponse.payload.items);
        articles = map(articlesResult);
    }

    const tariffsForPage = selectAndSortAvailableOnInternetPageTariffs({ isCottage: false })(options.store.getState());
    const table = generateTariffSummaryTableFAQ({
        tariffsForPage,
        code: code?.toString(),
        companyName,
    });
    articles.push(table);
    store.dispatch(setArticles(articlesToKeyDataMap(articles)));

    const addOpportunities = respAddOpportunities.isSuccess ? respAddOpportunities.payload : null;

    return {
        isRedesignInternetAbTestActive,
        addOpportunities,
        companyName,
        currentCity,
    };
};

export default InternetPage;
